// @ts-check
/** @typedef {{exportTemplate: import('../state').ExportTemplateState}} RootState */
import { createSelector } from 'reselect';
import { getSheetDefaultName, getSheetMetadata } from './metadata';

/**
 * @param {RootState} state */
export const modalView = state => state.exportTemplate.edit.view;

/**
 * @param {RootState} state */
export const isModalOpen = state => modalView(state) !== 'closed';

/**
 * Builds a deterministic JSON string from the editable fields of the template.
 * Used to compare current state against the snapshot taken at edit start.
 * @param {Record<string, import('../state').EditingSheet>} sheetMap
 * @param {string[]} sheetOrder
 * @returns {string}
 */
export const createEditFingerprint = (sheetMap, sheetOrder) => {
  const data = sheetOrder.map(sheetId => {
    const sheet = sheetMap[sheetId];
    return {
      id: sheetId,
      enabled: sheet.enabled,
      name: sheet.overridenName,
      columns: sheet.columnOrder.map(colId => {
        const col = sheet.columnMap[colId];
        return { id: colId, enabled: col.enabled, name: col.overridenName };
      }),
    };
  });
  return JSON.stringify(data);
};

/**
 * @param {RootState} state
 * @returns {boolean}
 */
export const isEditDirty = createSelector(
  [
    state => state.exportTemplate.edit.sheetMap,
    state => state.exportTemplate.edit.sheetOrder,
    state => state.exportTemplate.edit.snapshot,
  ],
  (sheetMap, sheetOrder, snapshot) => {
    if (!snapshot) return false;
    return createEditFingerprint(sheetMap, sheetOrder) !== snapshot;
  }
);

/**
 * @param {RootState} state */
export const isNewTemplate = state =>
  state.exportTemplate.edit.saveContext === 'new_template';

/**
 * @param {RootState} state */
export const isInSettingsView = state => modalView(state) === 'settings';

/**
 * @param {RootState} state
 * @returns {boolean}
 */
export const oneTimeExportAvailable = state =>
  state.exportTemplate.edit.context === 'search';

/**
 * @param {RootState} state
 * @returns {number | null}
 */
export const getExistingTemplateId = state =>
  state.exportTemplate.edit.sourceTemplateId;

/**
 * @param {RootState} state
 * @returns {string | null}
 */
export const getExistingTemplateName = state =>
  state.exportTemplate.edit.sourceTemplateName;

/**
 * @param {RootState} state
 * @returns {number[] | null}
 */
export const getExistingTemplateSharedWithIds = state =>
  state.exportTemplate.edit.sourceSharedWithIds;

/**
 * @param {RootState} state
 * @returns {boolean}
 */
export const doesUserOwnEditingTemplate = state => {
  return state.exportTemplate.edit.sourceTemplateIsOwner;
};

/**
 * @param {RootState} state
 * @returns {string}
 */
export const getEditingTemplateName = state => {
  return state.exportTemplate.edit.sourceTemplateName || 'All Fields';
};

/**
 * @param {RootState} state */
export const getSheetOrder = state => state.exportTemplate.edit.sheetOrder;

/**
 * @param {RootState} state
 * @returns {import('./state').ExportTemplateSheet[]}
 */
export const getEditingSheetsPayload = createSelector(
  [
    state => state.exportTemplate.edit.sheetOrder,
    state => state.exportTemplate.edit.sheetMap,
  ],
  (sheetOrder, sheetMap) => {
    const enabledSheets = sheetOrder.filter(sheetId => {
      const sheetRef = sheetMap[sheetId];
      if (sheetRef.selectedColumns === 0) return false;
      return sheetRef.enabled;
    });
    return enabledSheets.map(sheetId => {
      const sheet = sheetMap[sheetId];
      const enabledColumns = sheet.columnOrder.filter(columnId => {
        const col = sheet.columnMap[columnId];
        // Unavailable columns are hidden from the editor but round-trip through
        // save so a later licence re-upgrade restores the user's prior
        // selection. The Download Consumer drops them at export time via
        // ColumnRequirementEvaluator, so output is unaffected.
        return col.enabled || col.mandatory;
      });
      const toPayloadColumn = columnId => {
        const column = sheet.columnMap[columnId];
        return {
          columnMetadataId: column.columnMetadataId,
          overridenName: column.overridenName || null,
        };
      };
      // Orphan columns are appended so the backend stores them untouched;
      // when their metadata is reinstated they reappear naturally on next load.
      return {
        sheetMetadataId: sheetId,
        overridenName: sheet.overridenName || null,
        columns: [
          ...enabledColumns.map(toPayloadColumn),
          ...(sheet.orphanedColumnIds || []).map(toPayloadColumn),
        ],
      };
    });
  }
);

/**
 * @param {RootState} state
 * @returns {number}
 */
export const countEditingEnabledSheets = state => {
  let count = 0;
  for (const sheet of Object.values(state.exportTemplate.edit.sheetMap)) {
    if (sheet.enabled || sheet.mandatory) {
      count++;
    }
  }
  return count;
};

/**
 * @param {RootState} state
 * @returns {number}
 */
export const countEditingEnabledColumns = state => {
  let count = 0;
  for (const sheet of Object.values(state.exportTemplate.edit.sheetMap)) {
    if (sheet.enabled) {
      count += sheet.selectedColumns;
    }
  }
  return count;
};

/**
 * @param {RootState} state
 * @returns {number}
 */
export const countEditingTotalColumns = state => {
  let count = 0;
  for (const sheet of Object.values(state.exportTemplate.edit.sheetMap)) {
    if (sheet.enabled) {
      count += sheet.totalColumns;
    }
  }
  return count;
};

/**
 * @param {RootState} state
 * @returns {number}
 */
export const countEditingRenamedItems = state => {
  let count = 0;
  for (const sheet of Object.values(state.exportTemplate.edit.sheetMap)) {
    if (sheet.overridenName && sheet.defaultName !== sheet.overridenName) {
      count++;
    }
    for (const column of Object.values(sheet.columnMap)) {
      if (column.overridenName && column.defaultName !== column.overridenName)
        count++;
    }
  }
  return count;
};

// ========== Sheet Editing Selectors ==========

/**
 * @param {RootState} state */
export const getSheetsMap = state => state.exportTemplate.edit.sheetMap;

/**
 * Returns effective names of all sheets except the specified one.
 * Uses overridden name if set, otherwise falls back to default.
 * @param {RootState} state
 * @param {string} excludeSheetId
 * @returns {string[]}
 */
export const getOtherSheetNames = createSelector(
  [
    state => state.exportTemplate.edit.sheetOrder,
    state => state.exportTemplate.edit.sheetMap,
    getSheetMetadata,
    (_, excludeSheetId) => excludeSheetId,
  ],
  (sheetOrder, sheetMap, metadata, excludeSheetId) => {
    const names = [];
    for (const sheetId of sheetOrder) {
      if (sheetId === excludeSheetId) continue;
      const sheet = sheetMap[sheetId];
      if (sheet?.overridenName) {
        names.push(sheet.overridenName);
      } else {
        for (const meta of metadata) {
          if (meta.id === sheetId) {
            names.push(meta.name);
            break;
          }
        }
      }
    }
    return names;
  },
  {
    memoizeOptions: {
      maxSize: 50,
    },
  }
);

/**
 * @param {RootState} state
 * @param {string} sheetMetadataId
 * @returns {string}
 */
export const getEditingSheetName = (state, sheetMetadataId) => {
  const defaultSheetName = getSheetDefaultName(state, sheetMetadataId);
  const sheet = getEditingSheet(state, sheetMetadataId);
  return sheet?.overridenName || defaultSheetName;
};

/**
 * @param {RootState} state
 * @param {string | null} sheetMetadataId
 * @returns {import('../state').EditingSheet | null}
 */
export const getEditingSheet = (state, sheetMetadataId) => {
  if (sheetMetadataId === null) return null;
  return state.exportTemplate.edit.sheetMap[sheetMetadataId];
};

/**
 * @param {RootState} state
 * @param {string} sheetMetadataId
 * @returns {boolean}
 */
export const getIsSheetMandatory = (state, sheetMetadataId) => {
  return getEditingSheet(state, sheetMetadataId)?.mandatory || false;
};
